/**
 * Вложения вопросов FAQ: чем файл показывается и по какому адресу он лежит.
 *
 * Витрине принадлежит только публичный бакет faq-public. Файлы внутренних
 * аудиторий лежат в faq-internal, публичной ссылки у них нет и быть не должно.
 *
 * Адрес строится так же, как у картинок каталога: сначала путь без origin
 * (`/storage/v1/object/public/<bucket>/<path>`), дальше origin подставляет
 * либо загрузчик next/image (lib/image-loader.ts), либо resolveStorageUrl —
 * для всего, что идёт мимо него (<img>, <video>, ссылки на скачивание).
 */

import { resolveStorageUrl } from '@/lib/utils/resolve-storage-url';

export const FAQ_PUBLIC_BUCKET = 'faq-public';

export type FaqAttachmentPlacement = 'inline' | 'gallery';

export interface FaqAttachment {
  id: string;
  bucket: string;
  path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  /** inline — на файл ссылается текст ответа, gallery — показывается под ответом */
  placement: FaqAttachmentPlacement;
  alt: string | null;
  sort_order: number;
}

export type FaqAttachmentKind = 'image' | 'video' | 'file';

/**
 * Картинки — только то, что показывает любой браузер и умеет ужимать
 * рендер-эндпоинт storage. SVG, HEIC и прочее уходят в файлы на скачивание:
 * HEIC не открывается почти нигде, кроме Safari, а битая миниатюра хуже
 * честной ссылки на файл.
 */
const IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
]);

/** Видео, которые отдаём встроенному плееру. Остальное — файлом */
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime']);

export function attachmentKind(mimeType: string | null | undefined): FaqAttachmentKind {
  const type = (mimeType ?? '').split(';')[0].trim().toLowerCase();
  if (IMAGE_TYPES.has(type)) return 'image';
  if (VIDEO_TYPES.has(type)) return 'video';
  return 'file';
}

/** Путь без origin — вход для next/image, как у картинок каталога */
export function faqAttachmentPath(attachment: Pick<FaqAttachment, 'bucket' | 'path'>): string {
  const objectPath = attachment.path
    .replace(/^\/+/, '')
    .split('/')
    .map(encodeURIComponent)
    .join('/');
  return `/storage/v1/object/public/${attachment.bucket}/${objectPath}`;
}

/** Абсолютный адрес файла — для всего, что идёт мимо next/image */
export function faqAttachmentUrl(attachment: Pick<FaqAttachment, 'bucket' | 'path'>): string {
  return resolveStorageUrl(faqAttachmentPath(attachment));
}

/**
 * Ссылка на скачивание. Атрибут download у ссылки на другой origin браузер
 * игнорирует (storage живёт на api.*, сайт — на корневом домене), поэтому имя
 * файла просим у самого storage: с `?download=` он отдаёт
 * Content-Disposition: attachment.
 */
export function faqAttachmentDownloadUrl(
  attachment: Pick<FaqAttachment, 'bucket' | 'path' | 'file_name'>
): string {
  return `${faqAttachmentUrl(attachment)}?download=${encodeURIComponent(attachment.file_name)}`;
}

const SIZE_UNITS = ['KB', 'MB', 'GB'];

/** 532 B, 1.4 MB, 12 MB — как размер файла пишут в интерфейсах */
export function formatFileSize(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;

  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  // 1023.7 KB округлилось бы в «1024 KB» — это уже следующая единица
  if (rounded >= 1024 && unit < SIZE_UNITS.length - 1) return `1 ${SIZE_UNITS[unit + 1]}`;
  return `${rounded} ${SIZE_UNITS[unit]}`;
}
